import { PlacaNoCartaz } from './TrancaDeOrdem.js';
import { Relogio } from './Relogio.js';
import { fraseDaEspera, type Lancamento } from '../lib/lancamento.js';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * ── «SÓ VOCÊ VÊ» — O CARTAZ QUE EXISTE NA BANCADA E NÃO NA VITRINE ─────────
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **Ordem do dono, 04/11/2026:**
 *
 * > *«eu criei um curso de design patterns de agente IA e coloquei para ele não
 * > aparecer na vitrine e ele tem uma data pra ser exibido, mas eu coloquei ele
 * > no design da vitrine nos v-members, e ele deveria ao menos aparecer na
 * > vitrine da edição uma capa com aviso de que ele está agendado e bloqueado
 * > na sua visualização»*
 *
 * ## O que estava a acontecer, medido no banco de produção no mesmo dia
 *
 * `Padrões de Design para Agentes IA` — `status = 'draft'`, `available_at` dois
 * dias à frente, **quatro aulas publicadas lá dentro** — estava numa prateleira
 * manual («Engenharia de IA») que tinha esse item e mais nenhum. A régua do core
 * recusa o rascunho na primeira pergunta (*«um rascunho não é de ninguém»*), e
 * essa lei não se toca: ela é o que impede o curso em construção de aparecer na
 * turma inteira. O resultado, porém, era a prévia da bancada a desenhar a faixa
 * **vazia**, com a frase «solte aqui» — indistinguível de um arraste que falhou.
 *
 * ## Por que um carimbo PRÓPRIO, e não o «Agendado» que já existe
 *
 * Porque são dois fatos, e o tutor precisa dos dois:
 *
 * | carimbo | o que afirma | quem o lê |
 * |---|---|---|
 * | `SeloDeLancamento` («Agendado») | este curso abre num dia marcado | o ALUNO, na vitrine |
 * | `SeloSoVoceVe` («Só você vê») | este cartão não existe para o aluno | o TUTOR, na bancada |
 *
 * Um curso **anunciado** (publicado, com data no futuro) tem o primeiro e não o
 * segundo: o aluno vê o cartaz, trancado, com a data. Um curso **guardado**
 * (rascunho, com data) tem os dois fatos — e é por isso que este carimbo VENCE
 * o outro no canto da capa, enquanto a frase dentro do véu continua a ser a da
 * estreia. O teto de um carimbo por cartaz é lei desta prateleira desde que a
 * terceira espécie fechada nasceu, e quebrá-lo aqui empilharia duas chapas de
 * 10px no mesmo canto de um cartão de 176px.
 *
 * A ordem entre os dois não é arbitrária: «Agendado» sozinho faria o tutor
 * concluir que o aluno já vê o cartaz à espera do dia — que é exactamente o
 * engano que a ordem do dono veio corrigir, só do outro lado.
 *
 * ## Por que a redação mora AQUI, e não no core
 *
 * Porque este texto é do INSTRUMENTO, não do produto: ele só se desenha na
 * prévia da bancada, que é o painel, e nunca chega a aluno nenhum (o servidor só
 * manda o campo em `?fonte=rascunho`). É a mesma decisão — e a mesma gramática —
 * da faixa «Prévia — só quem tem acesso ao painel vê esta página», que vive em
 * `rotas/Promo.tsx` em português, sem passar pelo dicionário de seis línguas.
 *
 * O que o servidor manda é o FATO (`soVoceVe: true`), e nada mais. Mandar a
 * frase pronta obrigaria a rota a saber como a bancada escreve — e a frase da
 * estreia, essa sim, continua a ser derivada do core no relógio de quem lê.
 */

/** O carimbo sobre a arte. Vence o «Agendado» — ver o cabeçalho. */
export function SeloSoVoceVe() {
  return <PlacaNoCartaz data-vst-so-voce-ve="selo">Só você vê</PlacaNoCartaz>;
}

/**
 * A frase dentro do véu, no sítio onde a estreia e a tranca já falam.
 *
 * Com data marcada, ela é a da ESTREIA — derivada no relógio de quem lê, como
 * em qualquer outro cartaz agendado (`EstadoNoCartaz`), porque é a frase certa e
 * duas redações da mesma data divergiriam no primeiro dia em que uma mudasse.
 * Sem data, ela diz o estado que sobra: rascunho, e o gesto que o resolve.
 *
 * O relógio acompanha a data e **não** acompanha o rascunho: ali não há hora
 * nenhuma a anunciar, e o glifo prometeria uma.
 */
export function SoVoceVeNoCartaz({ lancamento }: { lancamento: Lancamento | null }) {
  if (!lancamento) {
    return <span className="truncate">Rascunho — publique para o aluno ver</span>;
  }
  return (
    <span className="flex items-center gap-1.5">
      <Relogio />
      <span data-vst-lancamento="frase" className="truncate">
        {fraseDaEspera(lancamento)}
      </span>
    </span>
  );
}
